import { BaseButton } from "@/components/atom/button/BaseButton";
import { BaseText } from "@/components/atom/text/BaseText";
import { FormTextInput } from "@/components/atom/textInput/form/FormTextInput";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { koToEnKeyboard } from "@/utils/koToEnKeyboard";
import { customNotification } from "@/utils/notificationShow";
import { Flex, TextInput } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

const BarcodeInput = Object.assign(
  {},
  {
    Template: FormTemplate,
    Subtitle: FormSubTitle,
    Input: FormTextInput,
    Text: BaseText,
    Button: BaseButton,
  }
);

export const BarcodeInputForm = () => {
  const { closeModal } = useModal();
  const [inputs, setInputs] = useState<string[]>([""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isComposing, setIsComposing] = useState(false); // ✅ IME 조합 상태

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === "Enter") {
      // @ts-ignore
      if (isComposing || event.nativeEvent?.isComposing) return; // ✅ 조합 중엔 보류
      event.preventDefault();

      const raw = inputs[index];
      const newValue = koToEnKeyboard(raw); // ✅ 정규화
      if (newValue === "") return;

      // ✅ 중복 검사도 정규화 기준(공백 제거, ko→en 반영)
      const isDuplicate = inputs.some(
        (v, i) => i !== index && koToEnKeyboard(v) === newValue
      );
      if (isDuplicate) {
        customNotification.error({
          message: `${newValue} ${COMMON_TEXT.NOTIFICATION.DUPLICATE_LOT}`,
        });
        setInputs((prev) => {
          const updated = [...prev];
          updated[index] = ""; // 중복 값 비우기
          return updated;
        });
        return;
      }

      // 현재 칸의 값을 정규화된 값으로 고정 반영
      setInputs((prev) => {
        const updated = [...prev];
        updated[index] = newValue;
        return updated;
      });

      // 다음 입력 필드 추가
      setInputs((prevInputs) => [...prevInputs, ""]);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    // @ts-ignore
    if (isComposing || event.nativeEvent?.isComposing) {
      // 조합 중에는 원문을 보관(조합 종료 시점에 최종글자 확정됨)
      const raw = event.target.value;
      setInputs((prev) => {
        const updated = [...prev];
        updated[index] = raw;
        return updated;
      });
      return;
    }

    // ✅ 타이핑 시점에서도 즉시 정규화(ko→en 포함)
    const normalized = koToEnKeyboard(event.target.value);
    setInputs((prev) => {
      const updated = [...prev];
      updated[index] = normalized;
      return updated;
    });
  };

  const handleRemoveInput = (index: number) => {
    setInputs((prevInputs) => prevInputs.filter((_, i) => i !== index));
    inputRefs.current = inputRefs.current.filter((_, i) => i !== index);
  };

  useEffect(() => {
    setInputs([""]);
    inputRefs.current = [];
  }, []);

  useEffect(() => {
    if (inputRefs.current.length > 0) {
      inputRefs.current[inputRefs.current.length - 1]?.focus();
    }
  }, [inputs]);

  const handleReturnBarcodeInputs = () => {
    // ✅ 최종 반환 전: 정규화 → 공백 제거 → 중복 제거(정규화 기준)
    const normalizedValues = inputs
      .map((v) => koToEnKeyboard(v))
      .filter((v) => v && v.length > 0);

    const deduped: string[] = [];
    normalizedValues.forEach((v) => {
      if (!deduped.includes(v)) deduped.push(v);
    });

    closeModal(deduped);
  };

  return (
    <BarcodeInput.Template
      onSubmit={handleReturnBarcodeInputs}
      onCancel={() => closeModal({})}
      label={COMMON_TEXT.LABEL.INPUT}
    >
      <Flex direction="column" gap="md" w="100%">
        {inputs.map((input, index) => (
          <Flex
            key={index}
            w="100%"
            justify={"flex-end"}
            gap={"xl"}
            align={"center"}
          >
            <BarcodeInput.Text>{index + 1}.</BarcodeInput.Text>
            <TextInput
              w={"100%"}
              size="sm"
              ref={(el) => (inputRefs.current[index] = el)}
              value={input}
              aria-label="Enter Input And Take a Barcode"
              data-autofocus
              lang="en"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              translate="no"
              onCompositionStart={() => setIsComposing(true)} // ✅ IME 시작
              onCompositionEnd={(e) => {
                setIsComposing(false); // ✅ IME 종료
                // 종료 시 확정된 글자를 정규화해서 반영
                const normalized = koToEnKeyboard(
                  (e.target as HTMLInputElement).value
                );
                setInputs((prev) => {
                  const updated = [...prev];
                  updated[index] = normalized;
                  return updated;
                });
              }}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onChange={(event) => handleChange(event, index)}
              disabled={index !== inputs.length - 1}
            />
            <BarcodeInput.Button
              disabled={!index}
              onClick={() => handleRemoveInput(index)}
              leftIcon={<IconTrash />}
              color="red"
              variant="white"
            >
              {COMMON_TEXT.BUTTON.CANCEL}
            </BarcodeInput.Button>
          </Flex>
        ))}
      </Flex>
    </BarcodeInput.Template>
  );
};
